import { Button, message, Table } from "antd";
import { useCallback, useMemo, useState } from "react";
import { DownloadOutlined } from "@ant-design/icons";
import dayjs from "../../../../utils/dayjs";
import PrintableImage from "../../../../components/shared/PrintableImage";
import { FILE_BASE_URL } from "../../../../config";
import FileIcon from "../../../../components/icons/FileIcon";
import { formatFileSize, getFullName } from "../../../../utils/shared";
import Avatar from "../../../../components/generic/Avatar";
import { useDispatch } from "react-redux";
import { handleDownload } from "../../../../utils/files";
import { updateFileKey } from "../../../../store/tasks/thunks";
import HtmlViewer from "../../../../components/shared/HtmlViewer";
import PdfViewer from "../../../../components/shared/PdfViewer";

const AttachmentsTable = ({ attachments, loading, task }) => {
  const dispatch = useDispatch();
  const [selectedFileId, setSelectedFileId] = useState(null);

  const onError = useCallback(
    async (task_attachment_id) => {
      return await dispatch(
        updateFileKey({
          task_attachment_id: task_attachment_id,
          tenant_id: task?.tenant_id,
          column_id: task?.column_id,
          task_id: task?.id,
        }),
      );
    },
    [task],
  );

  const downloadWithFallback = useCallback(
    async ({ storage_url, filename, id }) => {
      try {
        // First attempt
        const failed = await handleDownload(
          `${FILE_BASE_URL}${storage_url}`,
          filename,
        );

        if (!failed) return;

        const { payload } = await onError(id);

        if (!payload?.url) throw new Error("URL Not Found");

        const retryFailed = await handleDownload(
          `${FILE_BASE_URL}${payload.url}`,
          filename,
        );

        if (retryFailed) throw new Error("URL Not Found");
      } catch (error) {
        message.error("Download failed. Please try again after some time.");
      }
    },
    [onError],
  );

  const columns = useMemo(
    () => [
      {
        title: "Name",
        key: "filename",
        render: (_, record) => (
          <div className="printable-image">
            {record?.mime_type?.startsWith("image") ? (
              <PrintableImage
                onError={() => onError(record?.id)}
                src={`${FILE_BASE_URL}${record.storage_url}`}
                alt={record.filename}
                width={50}
                height={50}
                style={{
                  objectFit: "cover",
                  borderRadius: 6,
                }}
                isDoc
              />
            ) : (
              <FileIcon height={50} width={50} />
            )}

            <div>
              <div>{record.filename}</div>
            </div>
          </div>
        ),
      },
      {
        title: "Uploader",
        dataIndex: "uploader",
        key: "uploader",
        render: (uploader) => (
          <div className="flex uploader">
            <Avatar size={34} user={uploader} />
            {getFullName(uploader)}
          </div>
        ),
      },
      {
        title: "Size",
        dataIndex: "file_size_bytes",
        key: "file_size_bytes",
        render: (file_size_bytes) => formatFileSize(file_size_bytes),
      },
      {
        title: "Created At",
        dataIndex: "uploaded_at",
        key: "uploaded_at",
        render: (date) => dayjs(date).format("DD/MM/YYYY"),
      },
      {
        title: "Actions",
        key: "actions",
        width: 100,
        render: (_, record) => (
          <Button
            className="download-button"
            onClick={(e) => {
              e.stopPropagation();
              downloadWithFallback(record);
            }}
            icon={<DownloadOutlined />}
            color="primary"
            variant="dashed"
          />
        ),
      },
    ],
    [],
  );

  const selectedFile = useMemo(
    () => attachments?.find(({ id }) => selectedFileId === id),
    [selectedFileId, attachments],
  );

  return (
    <>
      <Table
        columns={columns}
        dataSource={attachments}
        rowKey="id"
        loading={loading}
        size="small"
        pagination={{ pageSize: 5 }}
        onRow={(record) => {
          return {
            className: "cursor-pointer",
            onClick: () => {
              if (!record) return;
              //@ts-ignore
              const { mime_type: mime = "", filename, id } = record || {};

              if (mime.includes("pdf") || mime.includes("html")) {
                return setSelectedFileId(id);
              }

              if (!mime.startsWith("image"))
                return message.info(
                  `No viewer available for file: ${filename}`,
                );
            },
          };
        }}
        scroll={{ x: "max-content", y: 400 }}
      />

      {selectedFile?.mime_type?.includes("pdf") ? (
        <PdfViewer
          key={selectedFile?.id}
          pdfUrl={`${FILE_BASE_URL}${selectedFile?.storage_url}`}
          onClose={() => setSelectedFileId(null)}
          onError={() => onError(selectedFile?.id)}
        />
      ) : selectedFile?.mime_type?.includes("html") ? (
        <HtmlViewer
          key={selectedFile?.id}
          url={`${FILE_BASE_URL}${selectedFile?.storage_url}`}
          onClose={() => setSelectedFileId(null)}
          onError={() => onError(selectedFile?.id)}
        />
      ) : (
        <></>
      )}
    </>
  );
};
export default AttachmentsTable;
